import { Fighter } from './fighter';

export type CellType = 'grass' | 'tree' | 'snag' | 'bush';

export interface GameCell {
  x: number;
  y: number;
  type: CellType;
  fighter: Fighter | null;
}

export interface GameState {
  map: GameCell[][];
  width: number;
  height: number;
  fighters: Fighter[];
  currentFighterIndex: number;
  selectedFighter: Fighter | null;
  selectedCell: { x: number; y: number } | null;
  gamePhase: 'menu' | 'setup' | 'battle' | 'gameOver';
  playerFighters: Fighter[];
  aiFighters: Fighter[];
}

export function createEmptyCell(x: number, y: number): GameCell {
  return {
    x,
    y,
    type: 'grass',
    fighter: null
  };
}

export function generateRandomMap(width: number, height: number): GameCell[][] {
  const map: GameCell[][] = [];

  for (let y = 0; y < height; y++) {
    const row: GameCell[] = [];
    for (let x = 0; x < width; x++) {
      const rand = Math.random();
      let type: CellType;

      // 70% grass, 15% tree, 5% snag, 10% bush
      if (rand < 0.70) {
        type = 'grass';
      } else if (rand < 0.85) {
        type = 'tree';
      } else if (rand < 0.90) {
        type = 'snag';
      } else {
        type = 'bush';
      }

      row.push({
        x,
        y,
        type,
        fighter: null
      });
    }
    map.push(row);
  }

  return map;
}

// Генерирует фоновый слой вокруг игрового поля (6 клеток в каждую сторону)
export function generateBackgroundLayer(gameWidth: number, gameHeight: number, borderSize: number = 6): GameCell[][] {
  const bgWidth = gameWidth + borderSize * 2;
  const bgHeight = gameHeight + borderSize * 2;
  const bgMap: GameCell[][] = [];

  for (let y = 0; y < bgHeight; y++) {
    const row: GameCell[] = [];
    for (let x = 0; x < bgWidth; x++) {
      // Проверяем, находится ли клетка в области игрового поля
      const isGameArea = x >= borderSize && x < gameWidth + borderSize && 
                        y >= borderSize && y < gameHeight + borderSize;
      
      if (isGameArea) {
        // В области игрового поля - пустая клетка (будет перекрыта игровым полем)
        row.push({
          x: x,
          y: y,
          type: 'grass',
          fighter: null
        });
      } else {
        // В фоновой области - рандомные непроходимые клетки
        const rand = Math.random();
        let type: CellType;
        
        // 40% grass, 30% tree, 15% snag, 15% bush (больше препятствий для фона)
        if (rand < 0.40) {
          type = 'grass';
        } else if (rand < 0.70) {
          type = 'tree';
        } else if (rand < 0.85) {
          type = 'snag';
        } else {
          type = 'bush';
        }

        row.push({
          x: x,
          y: y,
          type,
          fighter: null
        });
      }
    }
    bgMap.push(row);
  }

  return bgMap;
}

// Манхэттенское расстояние (для поиска пути)
export function getDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.abs(x1 - x2) + Math.abs(y1 - y2);
}

// Расстояние для движения (Chebyshev - диагональ = 1 очко)
export function getMoveDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = Math.abs(x1 - x2);
  const dy = Math.abs(y1 - y2);
  return Math.max(dx, dy);
}

// Проверяет, находится ли цель на прямой линии (горизонталь, вертикаль, диагональ)
export function isInLine(x1: number, y1: number, x2: number, y2: number): boolean {
  const dx = Math.abs(x1 - x2);
  const dy = Math.abs(y1 - y2);

  // Горизонталь, вертикаль или диагональ
  return dx === 0 || dy === 0 || dx === dy;
}

// Получает расстояние по прямой (для мечей и луков)
export function getLineDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = Math.abs(x1 - x2);
  const dy = Math.abs(y1 - y2);

  // Для прямой линии используем максимальное расстояние (Chebyshev distance)
  return Math.max(dx, dy);
}

export function getMovementCost(cell: GameCell): number {
  return cell.type === 'bush' ? 2 : 1;
}

export function isWalkable(cell: GameCell): boolean {
  return cell.type === 'grass' || cell.type === 'bush';
}

export function isTransparent(cell: GameCell): boolean {
  return cell.type !== 'tree'; // Только деревья блокируют видимость
}

// Проверяет, может ли атаковать по прямой/диагонали с учетом препятствий
export function canAttackInLine(attackerX: number, attackerY: number, targetX: number, targetY: number, range: number, map?: GameCell[][]): boolean {
  // 1. Проверка дистанции
  const dist = getLineDistance(attackerX, attackerY, targetX, targetY);
  if (dist > range) return false;

  // 2. Проверка геометрии (только прямые линии и диагонали)
  if (!isInLine(attackerX, attackerY, targetX, targetY)) return false;

  // 3. Проверка препятствий (Line of Sight)
  if (map && dist > 1) {
    const dx = Math.sign(targetX - attackerX);
    const dy = Math.sign(targetY - attackerY);

    let cx = attackerX + dx;
    let cy = attackerY + dy;

    while (cx !== targetX || cy !== targetY) {
      if (cx < 0 || cy < 0 || cy >= map.length || cx >= map[0].length) return false;

      const cell = map[cy][cx];
      if (!isTransparent(cell)) {
        return false; // Препятствие блокирует обзор
      }

      cx += dx;
      cy += dy;
    }
  }

  return true;
}

export function canMoveTo(map: GameCell[][], x: number, y: number): boolean {
  if (x < 0 || y < 0 || y >= map.length || x >= map[0].length) {
    return false;
  }
  const cell = map[y][x];
  return isWalkable(cell) && cell.fighter === null;
}

// Вычисляет путь от начальной позиции до целевой (A* алгоритм для обхода препятствий)
export function calculatePath(startX: number, startY: number, endX: number, endY: number, map: GameCell[][]): Array<{ x: number; y: number }> {
  // Если цель недостижима (не walkable или занята), возвращаем пустой путь
  if (!canMoveTo(map, endX, endY) && (endX !== startX || endY !== startY)) {
    // Но если это стартовая позиция, возвращаем пустой путь (уже на месте)
    return [];
  }

  // Простой A* алгоритм
  interface Node {
    x: number;
    y: number;
    g: number; // Стоимость пути от старта
    h: number; // Эвристика (расстояние до цели)
    f: number; // f = g + h
    parent: Node | null;
  }

  const openSet: Node[] = [];
  const closedSet = new Set<string>();
  
  const startNode: Node = {
    x: startX,
    y: startY,
    g: 0,
    h: getMoveDistance(startX, startY, endX, endY),
    f: getMoveDistance(startX, startY, endX, endY),
    parent: null
  };

  openSet.push(startNode);

  // Все возможные направления движения (8 направлений)
  const directions = [
    { dx: -1, dy: -1 }, // Диагональ влево-вверх
    { dx: 0, dy: -1 },  // Вверх
    { dx: 1, dy: -1 },  // Диагональ вправо-вверх
    { dx: -1, dy: 0 },  // Влево
    { dx: 1, dy: 0 },   // Вправо
    { dx: -1, dy: 1 },  // Диагональ влево-вниз
    { dx: 0, dy: 1 },   // Вниз
    { dx: 1, dy: 1 }    // Диагональ вправо-вниз
  ];

  while (openSet.length > 0) {
    // Находим узел с минимальным f
    let currentIndex = 0;
    for (let i = 1; i < openSet.length; i++) {
      if (openSet[i].f < openSet[currentIndex].f) {
        currentIndex = i;
      }
    }

    const current = openSet.splice(currentIndex, 1)[0];
    const currentKey = `${current.x},${current.y}`;
    closedSet.add(currentKey);

    // Если достигли цели, строим путь
    if (current.x === endX && current.y === endY) {
      const path: Array<{ x: number; y: number }> = [];
      let node: Node | null = current;
      while (node) {
        path.unshift({ x: node.x, y: node.y });
        node = node.parent;
      }
      // Убираем стартовую позицию из пути
      path.shift();
      return path;
    }

    // Проверяем соседей
    for (const dir of directions) {
      const newX = current.x + dir.dx;
      const newY = current.y + dir.dy;
      const newKey = `${newX},${newY}`;

      // Пропускаем, если уже в closedSet или нельзя пройти
      if (closedSet.has(newKey) || !canMoveTo(map, newX, newY)) {
          continue;
        }

      // Стоимость движения (диагональ = 1, как и обычное движение)
      const moveCost = getMovementCost(map[newY]?.[newX] || createEmptyCell(newX, newY));
      const g = current.g + moveCost;
      const h = getMoveDistance(newX, newY, endX, endY);
      const f = g + h;

      // Проверяем, есть ли уже этот узел в openSet
      const existingNode = openSet.find(n => n.x === newX && n.y === newY);
      if (existingNode) {
        // Если нашли лучший путь, обновляем
        if (g < existingNode.g) {
          existingNode.g = g;
          existingNode.f = f;
          existingNode.parent = current;
        }
      } else {
        // Добавляем новый узел
        openSet.push({
          x: newX,
          y: newY,
          g,
          h,
          f,
          parent: current
        });
    }
  }
  }

  // Путь не найден
  return [];
}

